import { css } from '@emotion/react';
import { theme } from '@frontend/theme';
import { Direction } from './chat-item-types';

const TIMESTAMP_HEIGHT_FACTOR = 3;

type ContainerStyleProps = {
  direction: Direction;
  showSenderName: boolean;
  showTimestamp: boolean;
  isFuture: boolean;
  isPaused: boolean;
  maxWidth: string;
  tagsRowMaxWidth: number;
  tagsRowHeight: number;
  hasTags: boolean;
};

export const containerStyles = ({
  direction,
  showSenderName,
  showTimestamp,
  isFuture,
  maxWidth,
  tagsRowMaxWidth,
  tagsRowHeight,
  hasTags,
}: ContainerStyleProps) => [
  css`
    width: 100%;
    display: flex;
    align-items: end;
    gap: ${theme.spacing(1)};
    list-style-type: none;
    margin: 0;
    padding: 0;
    position: relative;
    .avatar-container {
      padding-bottom: calc(
        ${theme.spacing(TIMESTAMP_HEIGHT_FACTOR)} + ${tagsRowHeight}px + ${hasTags ? theme.spacing(1) : '0px'}
      );
      width: 32;
      transition: padding-bottom 150ms ease-in-out;
    }
    .content-container {
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: ${direction === 'inbound' ? 'start' : 'end'};
    }
    .bubbles-container {
      max-width: ${maxWidth};
      display: flex;
      flex-direction: column;
      justify-content: start;
      gap: ${theme.spacing(1)};
      align-items: ${direction === 'inbound' ? 'start' : 'end'};
    }
    .metadata-container {
      height: ${theme.spacing(TIMESTAMP_HEIGHT_FACTOR)};
      display: flex;
      flex-direction: ${direction === 'inbound' ? 'row-reverse' : 'row'};
      gap: ${theme.spacing(1)};
      align-items: center;
      p {
        margin: 0;
      }
    }
    .status-container {
      display: flex;
      flex-direction: ${direction === 'inbound' ? 'row-reverse' : 'row'};
      gap: ${theme.spacing(0.5)};
      align-items: center;
    }
    .sender-name {
      transition: opacity 300ms ease-in-out;
      opacity: ${showSenderName ? 1 : 0};
    }
    .timestamp {
      transition: opacity 300ms ease-in-out;
      opacity: ${showTimestamp ? 1 : 0};
      color: ${isFuture ? theme.colors.warning60 : theme.colors.neutral50};
    }
    .tags-container {
      width: ${tagsRowMaxWidth}px;
      display: flex;
      gap: ${theme.spacing(1)};
      flex-wrap: wrap;
      justify-content: ${direction === 'inbound' ? 'start' : 'end'};
    }
    .tag-transition-container {
      padding-top: ${hasTags ? theme.spacing(1) : '0px'};
      min-height: calc(${tagsRowHeight}px - ${theme.spacing(1)});
      height: calc(${tagsRowHeight}px + ${hasTags ? theme.spacing(1) : '0px'});
      transition: min-height 150ms ease-in-out, height 150ms ease-in-out, padding-top 150ms ease-in-out;
    }
    .more-actions-button {
      transition: opacity 300ms ease-in-out;
      opacity: ${showSenderName ? 1 : 0};
    }
    .more-actions-icon {
      width: ${theme.spacing(2)};
      height: ${theme.spacing(2)};
    }
  `,
  direction === 'inbound'
    ? css`
        flex-direction: row;
        justify-content: start;
      `
    : css`
        flex-direction: row-reverse;
        justify-content: end;
      `,
];

type BubbleStyleProps = {
  direction: Direction;
  isInteractive: boolean;
  isFuture: boolean;
  isPaused: boolean;
  error: boolean;
  maxWidth?: string;
  customTextColor: boolean;
};

export const bubbleStyles = ({
  direction,
  isInteractive,
  isFuture,
  isPaused,
  customTextColor,
  error,
}: BubbleStyleProps) => [
  css`
    list-style-type: none;
    max-width: 100%;
    padding: ${theme.spacing(2)};
    display: flex;
    align-items: start;
    justify-content: start;
    gap: ${theme.spacing(2)};
    border-radius: ${theme.borderRadius.medium};
    background-color: ${direction === 'inbound' ? theme.colors.neutral5 : theme.colors.primary5};
    white-space: pre-line;
    :focus {
      outline: 3px solid ${theme.colors.primary50};
    }
    .bubble-content {
      display: flex;
      flex-direction: column;
      gap: ${theme.spacing(1)};
    }
    .bubble-text {
      user-select: ${isInteractive ? 'none' : 'auto'};
      margin: 0;
      word-break: break-word;
    }
  `,
  error &&
    css`
      background-color: ${theme.colors.critical5};
    `,
  isInteractive &&
    !isFuture &&
    css`
      cursor: pointer;
      background-color: ${theme.colors.white};
      outline: 1px solid ${theme.colors.primary50};
      color: ${theme.colors.primary50};
      transition: background-color 100ms ease-in-out, color 100ms ease-in-out;
      :hover {
        background-color: ${theme.colors.primary10};
      }
      :active {
        background-color: ${theme.colors.primary40};
        color: ${theme.colors.white};
      }
    `,
  isFuture &&
    (isPaused
      ? css`
          background-color: ${theme.colors.white};
          outline: 1px dashed ${theme.colors.neutral50};
          color: ${theme.colors.neutral50};
        `
      : css`
          background-color: ${theme.colors.warning5};
          color: ${theme.colors.warning80};
        `),
  !customTextColor &&
    css`
      .bubble-text {
        color: inherit;
      }
    `,
];

type ImageBubbleStyleProps = {
  isLoading: boolean;
  suppressPreview: boolean;
  isFuture: boolean;
  maxWidth?: string;
  maxHeight?: string;
};

export const imageBubbleStyles = ({
  isLoading,
  suppressPreview,
  isFuture,
  maxWidth = '320px',
  maxHeight = '480px',
}: ImageBubbleStyleProps) => [
  css`
    list-style-type: none;
    overflow: hidden;
    width: 'auto';
    height: ${isLoading ? `calc(${maxHeight} * 0.66)` : 'auto'};
    max-width: ${isLoading ? 'auto' : maxWidth};
    max-height: ${maxHeight};
    position: relative;
    border-radius: ${theme.borderRadius.medium};
    z-index: 1;
    transition: width 300ms ease-in-out, height 300ms ease-in-out;
    background-color: ${theme.colors.neutral10};
    figure {
      margin: 0;
    }
    img {
      display: block;
      max-width: 100%;
    }
    :focus {
      outline: 3px solid ${theme.colors.primary50};
    }
  `,
  !suppressPreview &&
    css`
      cursor: ${isLoading ? 'progress' : 'pointer'};
    `,
  isLoading &&
    css`
      figure {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: ${theme.spacing(2)};
        height: 100%;
        padding: ${theme.spacing(2)};
      }
      p {
        margin: 0;
      }
    `,
  isFuture &&
    css`
      background-color: ${theme.colors.primary10};
      color: ${theme.colors.primary70};
    `,
];

export const imagePreviewModalStyles = css`
  padding: 0;
  border-radius: ${theme.borderRadius.medium};
  max-width: unset;
  img {
    max-height: 90vh;
    max-width: 70vw;
    padding: 0;
  }
`;

export const imagePreviewModalIconStyles = css`
  position: absolute;
  top: -${theme.spacing(4)};
  right: 0;
  background-color: ${theme.colors.white};
`;

export const downloadIconStyle = css`
  position: absolute;
  top: ${theme.spacing(-4)};
  right: ${theme.spacing(4)};
  background-color: ${theme.colors.white};
`;

export const ChatItemStyles = {
  bubble: (bubbleProps: BubbleStyleProps) => bubbleStyles(bubbleProps),
  container: (containerProps: ContainerStyleProps) => containerStyles(containerProps),
  imageBubbleStyles: (imageBubbleProps: ImageBubbleStyleProps) => imageBubbleStyles(imageBubbleProps),
  imagePreviewModalStyles: imagePreviewModalStyles,
  imagePreviewModalIconStyles: imagePreviewModalIconStyles,
  downloadIconStyle: downloadIconStyle,
};
